import { useState, useRef, useEffect, useCallback } from 'react';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import debounce from 'transmute/debounce';
import styled from 'styled-components';
import { getInvoiceProperty, getInvoiceAssociatedObjectProperty } from 'invoices-ui-lib/utils/property';
import { fetchEmailContactOptions } from 'invoices-ui-lib/api/CrmSearchAPI';
import { isValidEmail } from 'invoices-ui-lib/utils/validation';
import RecordPaymentTabs from './RecordPaymentTabs';
import PortalIdParser from 'PortalIdParser';
import { COMMERCE_PAYMENT_TYPE_ID, CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { createCommercePaymentObject } from '../../api/paymentsAPI';
import { fromMoment } from 'foundations-components/transitional/utils/SimpleDate';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { CurrencyInput } from 'foundations-components/transitional/components/CurrencyInput';
import { Select } from 'foundations-components/transitional/components/Select';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { getOptions, getOptionsFromContacts } from 'invoices-ui-lib/utils/invoiceEmail';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { CommerceSpecialistsCustomRateAlert } from 'commerce-tools-ui-lib/public/components';
import { DIGITAL_PAYMENT_METHOD_VALUES } from '../../constants/payments';
import { CommerceTool } from 'commerce-tools-ui-lib/public/types';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DEBOUNCE_DELAY = 300;
const REFERENCE_NUMBER_MAX_CHARACTER_LIMIT = 50;
const validReferenceNumberRegExp = new RegExp('^[a-zA-Z0-9_/;:(). &?!\\\\-]*$');
const StyledDivBold = styled.div.withConfig({
  displayName: "RecordNewPaymentTab__StyledDivBold",
  componentId: "sc-4fvtse-0"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    fontWeight: theme.typography['code-100'].fontWeight
  };
});
const PAYMENT_AMOUNT_VALIDATION_ERROR = {
  INVALID_PAYMENT_AMOUNT: 'INVALID_PAYMENT_AMOUNT'
};
const getInitialPaymentRecord = invoice => ({
  associatedObjects: {
    COMMERCE_PAYMENT_TO_INVOICE: [invoice],
    COMMERCE_PAYMENT_TO_CONTACT: invoice.associatedObjects.INVOICE_TO_CONTACT[0] ? [invoice.associatedObjects.INVOICE_TO_CONTACT[0]] : []
  },
  objectTypeId: COMMERCE_PAYMENT_TYPE_ID,
  portalId: PortalIdParser.get(),
  objectId: null,
  properties: {
    hs_initial_amount: {
      name: 'hs_initial_amount',
      value: getInvoiceProperty(invoice, 'hs_balance_due')
    },
    hs_initiated_date: {
      name: 'hs_initiated_date',
      value: I18n.moment.userTz().startOf('day').valueOf()
    },
    hs_payment_method_type: {
      name: 'hs_payment_method_type',
      value: ''
    },
    hs_reference_number: {
      name: 'hs_reference_number',
      value: ''
    },
    hs_internal_comment: {
      name: 'hs_internal_comment',
      value: ''
    }
  }
});
const formatContactDisplayName = ({
  name,
  email
}) => name && email ? `${email} (${name})` : email ? `${email}` : `(${name})`;
const getReferenceNumberValidationMessage = referenceNumberValue => {
  if (!referenceNumberValue) return null;
  if (!validReferenceNumberRegExp.test(referenceNumberValue)) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoices.components.modals.recordPayment.referenceNumber.validation.invalid"
    });
  } else if (referenceNumberValue.length > REFERENCE_NUMBER_MAX_CHARACTER_LIMIT) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "invoices.components.modals.recordPayment.referenceNumber.validation.overMaxLength"
    });
  }
  return null;
};
const getPaymentAmountValidationError = ({
  paymentAmount,
  invoice
}) => {
  const paymentAmountAsNumber = Number(paymentAmount);
  const balanceDueAsNumber = Number(getInvoiceProperty(invoice, 'hs_balance_due'));
  switch (true) {
    case !paymentAmountAsNumber:
    case paymentAmountAsNumber > balanceDueAsNumber:
      return PAYMENT_AMOUNT_VALIDATION_ERROR.INVALID_PAYMENT_AMOUNT;
    default:
      return null;
  }
};
const PaymentAmountValidationErrorMessage = ({
  validationError
}) => {
  switch (validationError) {
    case PAYMENT_AMOUNT_VALIDATION_ERROR.INVALID_PAYMENT_AMOUNT:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.components.modals.recordPayment.amount.validation.invalidPaymentAmount"
      });
    default:
      return null;
  }
};
const getContactEmailValidationMessage = paymentAssociatedContact => {
  if (!paymentAssociatedContact) return null;
  return isValidEmail(getInvoiceAssociatedObjectProperty(paymentAssociatedContact, 'email')) ? null : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoices.components.modals.recordPayment.customerEmail.validation.invalidEmail"
  });
};
const RecordNewPaymentTab = props => {
  const {
    onSuccess,
    onFailure,
    onReject,
    invoice,
    selectedTab,
    setSelectedTab,
    paymentMethodOptions,
    shouldDisablePartialPayments
  } = props;
  const [paymentRecord, setPaymentRecord] = useState(getInitialPaymentRecord(invoice));
  const [contacts, setContacts] = useState(invoice.associatedObjects.INVOICE_TO_CONTACT[0] ? [invoice.associatedObjects.INVOICE_TO_CONTACT[0]] : []);
  const [referenceNumberError, setReferenceNumberError] = useState(false);
  const [paymentAmountError, setPaymentAmountError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldSendReceipt, setShouldSendReceipt] = useState(true);
  const currentDate = I18n.moment.userTz();
  const oneYearAgo = I18n.moment(currentDate).subtract(1, 'year');
  const currency = getInvoiceProperty(invoice, 'hs_currency');
  const paymentDate = paymentRecord.properties.hs_initiated_date.value;
  const paymentAssociatedContact = paymentRecord.associatedObjects.COMMERCE_PAYMENT_TO_CONTACT[0];
  const referenceNumber = paymentRecord.properties.hs_reference_number.value;
  const paymentAmount = paymentRecord.properties.hs_initial_amount.value;
  const lastEditDate = getInvoiceProperty(invoice, 'hs_last_edit_date');
  const debouncedReferenceNumberCheck = useRef(debounce(DEBOUNCE_DELAY, newReferenceNumber => {
    setReferenceNumberError(!!getReferenceNumberValidationMessage(newReferenceNumber));
  })).current;
  const debouncedPaymentAmountCheck = useRef(debounce(DEBOUNCE_DELAY, newPaymentAmount => {
    setPaymentAmountError(getPaymentAmountValidationError({
      paymentAmount: newPaymentAmount,
      invoice
    }));
  })).current;
  useEffect(() => {
    debouncedReferenceNumberCheck(referenceNumber);
  }, [referenceNumber, debouncedReferenceNumberCheck]);
  useEffect(() => {
    debouncedPaymentAmountCheck(paymentAmount);
  }, [paymentAmount, debouncedPaymentAmountCheck]);
  const debouncedHandleLoadOptions = useRef(debounce(DEBOUNCE_DELAY, (searchQuery, loadOptionsCallback) => {
    if (!searchQuery.length) {
      loadOptionsCallback(null);
      return;
    }
    fetchEmailContactOptions(searchQuery).then(fetchedContacts => {
      setContacts(fetchedContacts);
      const options = getOptions(searchQuery, fetchedContacts, formatContactDisplayName);
      loadOptionsCallback(null, {
        options
      });
    })
    // This would be a failure during the user input-triggered search. On failure,
    // swallow the error quietly so the user can hopefully recover by continuing to type.
    .catch(() => {});
  })).current;
  useEffect(() => {
    if (paymentMethodOptions.length) {
      setPaymentRecord(existingPaymentRecord => Object.assign({}, existingPaymentRecord, {
        properties: Object.assign({}, existingPaymentRecord.properties, {
          hs_payment_method_type: {
            name: 'hs_payment_method_type',
            value: paymentMethodOptions.find(option => option.value === 'check') ? 'check' : paymentMethodOptions[0].value
          }
        })
      }));
    }
  }, [paymentMethodOptions]);
  const hasPaymentAmountValidationError = !!paymentAmountError;
  const shouldDisableCreate = isLoading || !paymentRecord.properties.hs_initiated_date.value || !paymentAssociatedContact || !isValidEmail(getInvoiceAssociatedObjectProperty(paymentAssociatedContact, 'email')) || referenceNumberError || !paymentRecord.properties.hs_payment_method_type.value || hasPaymentAmountValidationError;
  const handleChangeContactAssociation = useCallback(event => {
    const newSelectedContact = contacts.find(contactOption => contactOption.objectId === event.target.value) || {
      objectId: null,
      objectTypeId: CONTACT_TYPE_ID,
      properties: {
        email: {
          name: 'email',
          value: event.target.value
        }
      },
      portalId: PortalIdParser.get()
    };
    setPaymentRecord(Object.assign({}, paymentRecord, {
      associatedObjects: Object.assign({}, paymentRecord.associatedObjects, {
        COMMERCE_PAYMENT_TO_CONTACT: [newSelectedContact]
      })
    }));
  }, [contacts, paymentRecord]);
  const handleOnClick = useCallback(() => {
    setIsLoading(true);
    createCommercePaymentObject({
      commercePayment: paymentRecord,
      lastEditDate,
      shouldSendReceipt
    }).then(onSuccess).catch(onFailure);
  }, [onFailure, onSuccess, paymentRecord, setIsLoading, lastEditDate, shouldSendReceipt]);
  const getShouldShowCustomRateAlert = useCallback(() => DIGITAL_PAYMENT_METHOD_VALUES.includes(paymentRecord.properties.hs_payment_method_type.value), [paymentRecord.properties.hs_payment_method_type.value]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(RecordPaymentTabs, {
        selectedTab: selectedTab,
        setSelectedTab: setSelectedTab,
        shouldDisableTabs: isLoading
      }), /*#__PURE__*/_jsx(StyledDivBold, {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "invoices.components.modals.recordPayment.balanceDue_jsx",
          elements: {
            FormattedCurrency: FormattedIntlCurrency
          },
          options: {
            currencyCode: currency,
            value: +getInvoiceProperty(invoice, 'hs_balance_due')
          }
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.recordPayment.amount.label"
        }),
        validationMessage: /*#__PURE__*/_jsx(PaymentAmountValidationErrorMessage, {
          validationError: paymentAmountError
        }),
        error: hasPaymentAmountValidationError,
        paddingBlockStart: "400",
        children: /*#__PURE__*/_jsx(Tooltip, {
          disabled: !shouldDisablePartialPayments,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoices.components.modals.recordPayment.amount.disabled.partialPayment"
          }),
          children: /*#__PURE__*/_jsx(CurrencyInput, {
            "data-test-id": "record-payment-panel-amount-field",
            value: +paymentRecord.properties.hs_initial_amount.value,
            currency: currency,
            readOnly: shouldDisablePartialPayments,
            onChange: ({
              target: {
                value
              }
            }) => setPaymentRecord(Object.assign({}, paymentRecord, {
              properties: Object.assign({}, paymentRecord.properties, {
                hs_initial_amount: {
                  name: 'hs_initial_amount',
                  value
                }
              })
            })),
            min: 0
          })
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.recordPayment.paymentDate.label"
        }),
        children: /*#__PURE__*/_jsx(DateInput, {
          value: paymentDate ? fromMoment(I18n.moment(paymentDate)) : paymentDate,
          min: fromMoment(oneYearAgo),
          max: fromMoment(currentDate),
          onChange: newDate => {
            const value = newDate.target.value;
            setPaymentRecord(Object.assign({}, paymentRecord, {
              properties: Object.assign({}, paymentRecord.properties, {
                hs_initiated_date: {
                  name: 'hs_initiated_date',
                  value: value ? I18n.moment(value).valueOf() : value
                }
              })
            }));
          }
        })
      }), /*#__PURE__*/_jsx(CommerceSpecialistsCustomRateAlert, {
        appName: CommerceTool.INVOICES,
        location: "invoice-record-new-payment-panel",
        use: "inline",
        condition: getShouldShowCustomRateAlert
      }), /*#__PURE__*/_jsx(FormControl, {
        "data-test-id": "record-payment-panel-payment-method-select",
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.recordPayment.paymentMethod.label"
        }),
        children: paymentMethodOptions.length ? /*#__PURE__*/_jsx(Select, {
          value: paymentRecord.properties.hs_payment_method_type.value,
          options: paymentMethodOptions,
          onChange: event => setPaymentRecord(Object.assign({}, paymentRecord, {
            properties: Object.assign({}, paymentRecord.properties, {
              hs_payment_method_type: {
                name: 'hs_payment_method_type',
                value: event.target.value
              }
            })
          }))
        }) : /*#__PURE__*/_jsx(LoadingSpinner, {})
      }), /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.recordPayment.referenceNumber.label"
        }),
        error: referenceNumberError,
        validationMessage: referenceNumberError && getReferenceNumberValidationMessage(referenceNumber),
        children: /*#__PURE__*/_jsx(TextInput, {
          "data-test-id": "record-payment-panel-reference-number",
          value: referenceNumber,
          onChange: ({
            target: {
              value
            }
          }) => setPaymentRecord(Object.assign({}, paymentRecord, {
            properties: Object.assign({}, paymentRecord.properties, {
              hs_reference_number: {
                name: 'hs_reference_number',
                value
              }
            })
          })),
          placeholder: unescapedText('invoices.components.modals.recordPayment.referenceNumber.placeholder')
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.recordPayment.internalNote.label"
        }),
        help: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.recordPayment.internalNote.helpText"
        }),
        children: /*#__PURE__*/_jsx(TextArea, {
          value: paymentRecord.properties.hs_internal_comment.value,
          placeholder: unescapedText('invoices.components.modals.recordPayment.internalNote.placeholder'),
          onChange: event => setPaymentRecord(Object.assign({}, paymentRecord, {
            properties: Object.assign({}, paymentRecord.properties, {
              hs_internal_comment: {
                name: 'hs_internal_comment',
                value: event.target.value
              }
            })
          }))
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.recordPayment.customerEmail.label"
        }),
        help: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.recordPayment.customerEmail.help"
        }),
        required: true,
        error: !!getContactEmailValidationMessage(paymentAssociatedContact),
        validationMessage: getContactEmailValidationMessage(paymentAssociatedContact),
        children: /*#__PURE__*/_jsx(Select, {
          onChange: handleChangeContactAssociation,
          loadOptions: debouncedHandleLoadOptions,
          value: paymentAssociatedContact ? paymentAssociatedContact.objectId || paymentAssociatedContact.properties.email.value : undefined,
          options: invoice.associatedObjects.INVOICE_TO_CONTACT[0] ? getOptionsFromContacts([invoice.associatedObjects.INVOICE_TO_CONTACT[0]], formatContactDisplayName) : []
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        children: /*#__PURE__*/_jsx(Checkbox, {
          "data-test-id": "send-receipt-checkbox",
          onChange: () => setShouldSendReceipt(previousState => !previousState),
          checked: shouldSendReceipt,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoices.components.modals.recordPayment.sendEmail"
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        use: "primary",
        onClick: handleOnClick,
        disabled: shouldDisableCreate,
        "data-test-id": "invoice-record-payment-panel-confirm-button",
        loading: isLoading,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.recordPayment.confirmButton"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onReject,
        disabled: isLoading,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.components.modals.recordPayment.cancelButton"
        })
      })]
    })]
  });
};
export default RecordNewPaymentTab;